/**
 * Shared internals behind DateRangePicker (two-ended) and DatePicker (single-date, 31/7) — pulled out of
 * DateRangePicker.tsx rather than copy-pasted so the two widgets can never quietly drift on the parts that
 * are genuinely identical: the masked DD/MM/YYYY digit entry, the position:fixed popover mechanics (open/
 * close, outside-dismiss, Escape, scroll/resize-close, focus-into-popover-on-open — see DateRangePicker's
 * own r12 comment for WHY fixed, not absolute), and the calendar grid's month/year nav + roving-tabindex
 * keyboard navigation.
 *
 * DateRangePicker's public API and behavior are UNCHANGED by this extraction — everything here is the
 * exact logic that used to live inline in DateRangePicker.tsx, moved verbatim (pre-extraction shape is in
 * git history at 3dbf572 if a diff is ever needed).
 */
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
  type SetStateAction,
} from "react";
import {
  addDays,
  addMonths,
  addYears,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isValid,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

export const iso = (d: Date) => format(d, "yyyy-MM-dd");
export const isoToDMY = (s: string) => {
  const d = parseISO(s);
  return isValid(d) ? format(d, "dd/MM/yyyy") : "";
};
export const prettyIso = (s: string) => {
  const d = parseISO(s);
  return isValid(d) ? format(d, "MMM d, yyyy") : "—";
};
export function clamp(s: string, min?: string, max?: string): string {
  if (min && s < min) return min;
  if (max && s > max) return max;
  return s;
}

/** Digit-only DD/MM/YYYY masking — type continuous digits, auto-slashes appear as they're earned. Returns
 *  both the display string (for the input's controlled `value`) and the raw digit run (callers build the
 *  ISO string themselves once all 8 digits are in, since range vs single differ on what happens next). */
export function maskDigits(raw: string): { formatted: string; digits: string } {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  let formatted = digits.slice(0, 2);
  if (digits.length > 2) formatted += "/" + digits.slice(2, 4);
  if (digits.length > 4) formatted += "/" + digits.slice(4, 8);
  return { formatted, digits };
}

/** Single-open-popover registry (module-level, not per-component state — it has to be shared across
 *  every DatePicker/DateRangePicker instance on the page): the close fn of whichever picker popover is
 *  currently open, or null. Lets a second picker's open effect close a first one that's still open
 *  (f1-dual-dialog, adversarial review 31/7 — tabbing from an open DateRangePicker popover into a
 *  DatePicker and opening IT left both open simultaneously, since neither widget knew about the other).
 *  Deliberately NOT an onBlur/focusout close — see the comments in DateRangePicker.tsx and
 *  DatePicker.tsx on why that regresses disabled-day clicks (a disabled button fires no mousedown yet
 *  still bounces focus outside the widget). */
let activePopoverClose: (() => void) | null = null;

/**
 * Position:fixed popover mechanics — coords computed from the trigger rect, flip-up when the space below
 * is tighter than above, hugging the right edge on narrow viewports. Fixed (not absolute-in-flow) because
 * `overflow-y-auto` on a Modal body clips an absolutely-positioned popover's head/tail (reproduced on an
 * 800px screen); fixed escapes every clipping ancestor and still sits on the native `<dialog>`'s top-layer
 * as long as the element stays IN the dialog's own DOM subtree — never portal this to `document.body`, or
 * it sinks under `<dialog>::backdrop`. The one precondition fixed positioning needs (no ancestor holding a
 * stale `transform`) already holds for Modal: its open/close animation doesn't leave a transform behind
 * once the fill-mode settles.
 *
 * Closes on outside mousedown, Escape (returning focus to the trigger), any scroll/resize (capture:
 * true so a scrolling ANCESTOR — e.g. the Modal body — is caught even though scroll doesn't bubble;
 * coordinates would otherwise go stale under the pointer), AND on another picker popover opening (the
 * single-open-popover registry above). Moves focus into the popover's first input on open, since the
 * typed masked field is the primary keyboard entry path.
 */
export function useFixedPopover({
  open,
  onClose,
  containerRef,
  triggerRef,
  popoverRef,
}: {
  open: boolean;
  onClose: () => void;
  containerRef: RefObject<HTMLElement | null>;
  triggerRef: RefObject<HTMLElement | null>;
  popoverRef: RefObject<HTMLElement | null>;
}): { top: number; left: number } | null {
  useEffect(() => {
    if (!open) return;
    // Only one picker popover may be open at a time — close whichever one was previously registered
    // (a no-op if none was, or if it's somehow already this same popover) before claiming the slot.
    if (activePopoverClose && activePopoverClose !== onClose) activePopoverClose();
    activePopoverClose = onClose;

    const raf = requestAnimationFrame(() => popoverRef.current?.querySelector("input")?.focus());
    function onDoc(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      // Only clear the slot if it's still ours — a newer popover may have already claimed it (the
      // branch above), and this cleanup must not stomp on that registration.
      if (activePopoverClose === onClose) activePopoverClose = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const pop = popoverRef.current;
    if (!trigger || !pop) return;
    const rect = trigger.getBoundingClientRect();
    const gap = 8; // ~0.5rem
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const flip = spaceBelow < pop.offsetHeight + gap && spaceAbove > spaceBelow;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - pop.offsetWidth - 8));
    setCoords({
      top: flip ? Math.max(8, rect.top - pop.offsetHeight - gap) : rect.bottom + gap,
      left,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const close = () => onClose();
    // capture:true — scroll ไม่ bubble แต่ capture จับ scroll ของ container ลูกทุกตัว (รวม Modal body)
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return coords;
}

export const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

const navBtnClass =
  "grid size-7 place-items-center rounded-(--radius-sm) text-(--text-muted) hover:bg-(--surface-hover) hover:text-(--text)";

/** Month/year nav row + centered month label — identical in both widgets. */
export function CalendarHeader({
  month,
  setMonth,
}: {
  month: Date;
  setMonth: Dispatch<SetStateAction<Date>>;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          aria-label="Previous year"
          onClick={() => setMonth((m) => addYears(m, -1))}
          className={navBtnClass}
        >
          <ChevronsLeft className="size-4" aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => setMonth((m) => addMonths(m, -1))}
          className={navBtnClass}
        >
          <ChevronLeft className="size-4" aria-hidden />
        </button>
      </div>
      <span className="text-(length:--text-sm) font-semibold text-(--text-strong)">
        {format(month, "MMMM yyyy")}
      </span>
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          aria-label="Next month"
          onClick={() => setMonth((m) => addMonths(m, 1))}
          className={navBtnClass}
        >
          <ChevronRight className="size-4" aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Next year"
          onClick={() => setMonth((m) => addYears(m, 1))}
          className={navBtnClass}
        >
          <ChevronsRight className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}

/**
 * The day grid's month contents + roving-tabindex keyboard navigation (arrows, Home/End, PageUp/PageDown
 * for month hop) — identical between the range Calendar (two-click anchor selection) and DatePicker's
 * single-click Calendar; only what a click on a day DOES differs, which stays in each caller's own `pick`.
 */
export function useCalendarNav({
  month,
  setMonth,
  min,
  max,
  initialFocusedDate,
}: {
  month: Date;
  setMonth: Dispatch<SetStateAction<Date>>;
  min?: string | undefined;
  max?: string | undefined;
  // The day the roving-tabindex target starts on — the actual selected/anchor date the caller opened on
  // (e.g. the range's end date, or the single value), NOT `startOfMonth(month)`. A Tab into a freshly
  // opened calendar should land on the day the operator already picked, not day 1 of whatever month
  // happens to contain it.
  initialFocusedDate: Date;
}) {
  const gridRef = useRef<HTMLDivElement>(null);
  // Roving-tabindex target: exactly one day owns tabIndex 0 so the grid is reachable + arrow-navigable.
  const [focusedDate, setFocusedDate] = useState<Date>(() => initialFocusedDate);

  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
    const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [month]);

  const clampToRange = (d: Date): Date => {
    const s = iso(d);
    if (min && s < min) return parseISO(min);
    if (max && s > max) return parseISO(max);
    return d;
  };

  // Keep the roving-tabindex target inside the visible month (e.g. after month/year nav-button clicks)
  // and within the allowed window, so a Tab into the grid always lands on a focusable day.
  useEffect(() => {
    setFocusedDate((f) => (isSameMonth(f, month) ? f : clampToRange(startOfMonth(month))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, min, max]);

  function moveFocus(target: Date) {
    const next = clampToRange(target);
    setFocusedDate(next);
    if (!isSameMonth(next, month)) setMonth(startOfMonth(next));
    // Uncancelled, unlike the popover-open rAF at the top of this file — and deliberately so. That one
    // lives in an effect that can re-run, so a stale frame there would fight the next one for focus;
    // this fires from a key handler with nothing to hang a cleanup on. Audited 2026-09-18 with the
    // setTimeout sweep: the callback only reads a ref and calls focus(), writing no state and touching
    // no `window`, so a frame landing after unmount is a no-op on a detached node.
    requestAnimationFrame(() =>
      gridRef.current?.querySelector<HTMLButtonElement>(`[data-day="${iso(next)}"]`)?.focus(),
    );
  }

  function onGridKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    let target: Date | null = null;
    switch (e.key) {
      case "ArrowLeft":
        target = addDays(focusedDate, -1);
        break;
      case "ArrowRight":
        target = addDays(focusedDate, 1);
        break;
      case "ArrowUp":
        target = addDays(focusedDate, -7);
        break;
      case "ArrowDown":
        target = addDays(focusedDate, 7);
        break;
      case "Home":
        target = startOfWeek(focusedDate, { weekStartsOn: 0 });
        break;
      case "End":
        target = endOfWeek(focusedDate, { weekStartsOn: 0 });
        break;
      case "PageUp":
        target = addMonths(focusedDate, -1);
        break;
      case "PageDown":
        target = addMonths(focusedDate, 1);
        break;
      default:
        return;
    }
    e.preventDefault();
    moveFocus(target);
  }

  return { days, focusedDate, setFocusedDate, gridRef, onGridKeyDown };
}
